import React, { useState } from 'react';
import MappingForm from '../../components/admin/mapping/MappingForm';
import { useData } from '../../context/DataContext';
import { useNavigate } from 'react-router-dom';

const MappingPage = () => {
  const { addMapping } = useData();
  const navigate = useNavigate();
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedFarmers, setSelectedFarmers] = useState([]);

  const handleSubmit = async (mappingData) => {
    setError(null);
    setSuccess(null);

    if (!mappingData || !mappingData.company || !mappingData.farmers || mappingData.farmers.length === 0) {
      setError('Please select both a company and at least one farmer');
      return;
    }

    try {
      console.log('Creating mappings with data:', mappingData);
      const results = [];

      for (const farmer of mappingData.farmers) {
        try {
          const result = await addMapping(mappingData.company, farmer.id);
          results.push({
            farmerId: farmer.id,
            farmerName: farmer.name || farmer.farmer_name,
            success: result.success,
            error: result.error
          });
        } catch (error) {
          console.error(`Error creating mapping for farmer ${farmer.id}:`, error);
          results.push({
            farmerId: farmer.id,
            farmerName: farmer.name || farmer.farmer_name,
            success: false,
            error: error.message
          });
        }
      }

      console.log('Mapping results:', results);

      // Check if any mappings were successful
      const successfulMappings = results.filter(r => r.success);
      const failedMappings = results.filter(r => !r.success);

      if (successfulMappings.length > 0) {
        setSuccess(`${successfulMappings.length} mapping(s) created successfully`);
        // Navigate to mapped data page after successful mapping
        setTimeout(() => {
          navigate('/admin/mapped-data');
        }, 2000);
      }

      if (failedMappings.length > 0) {
        const errorMessages = failedMappings.map(m => 
          `${m.farmerName}: ${m.error}`
        ).join('\n');
        setError(errorMessages);
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setError(error.message || 'An error occurred while creating mappings');
    }
  };

  const handleCancel = () => {
    navigate('/admin/dashboard');
  };

  return (
    <div style={styles.container}>
      <div style={styles.pageHeader}>
        <div style={styles.titleContainer}>
          <span style={styles.titleIcon}>🗺️</span>
          <h1 style={styles.title}>Create Mappings</h1>
        </div>
        <p style={styles.subtitle}>
          Connect farmers with companies to establish partnerships
        </p>
      </div>

      {success ? (
        <div style={styles.successMessage}>
          <div style={styles.successIcon}>✅</div>
          <h2 style={styles.successTitle}>Mappings Created Successfully!</h2>
          <p style={styles.successText}>{success}</p>
          <p style={styles.redirectText}>Redirecting to Mapped Data page...</p>
        </div>
      ) : (
        <>
          {error && (
            <div style={styles.errorMessage}>
              <div style={styles.errorIcon}>⚠️</div>
              <p style={styles.errorText}>{error}</p>
            </div>
          )}

          {loading ? (
            <div style={styles.loadingContainer}>
              <div style={styles.loadingSpinner}></div>
              <p style={styles.loadingText}>Creating mappings...</p>
            </div>
          ) : (
            <MappingForm onSubmit={handleSubmit} onCancel={handleCancel} />
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  pageHeader: {
    marginBottom: '1.5rem',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '1rem',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  titleIcon: {
    fontSize: '2rem',
    marginRight: '0.75rem',
    color: 'var(--primary-color)',
  },
  title: {
    margin: 0,
    color: 'var(--text-color)',
    fontSize: '2rem',
    fontWeight: 'bold',
  },
  subtitle: {
    margin: '0.5rem 0 0 0',
    color: 'var(--text-light)',
    fontSize: '1rem',
  },
  successMessage: {
    backgroundColor: 'var(--success-color)',
    color: 'white',
    padding: '3rem 2rem',
    borderRadius: '12px',
    textAlign: 'center',
    boxShadow: 'var(--card-shadow)',
    maxWidth: '600px',
    margin: '0 auto',
    backgroundImage: 'linear-gradient(135deg, var(--success-color) 0%, #2ecc71 100%)',
  },
  successIcon: {
    fontSize: '4rem',
    marginBottom: '1rem',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 auto 1.5rem auto',
  },
  successTitle: {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
  },
  successText: {
    fontSize: '1.1rem',
    marginBottom: '1.5rem',
    opacity: 0.9,
  },
  redirectText: {
    fontSize: '0.9rem',
    opacity: 0.8,
    fontStyle: 'italic',
  },
  errorMessage: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '1.5rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
  },
  errorIcon: {
    fontSize: '2rem',
    marginRight: '1rem',
  },
  errorText: {
    fontSize: '1rem',
    margin: 0,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  loadingSpinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid var(--primary-color)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '1.5rem',
  },
  loadingText: {
    fontSize: '1.2rem',
    color: 'var(--text-color)',
    margin: 0,
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
};

export default MappingPage;
